/*
 * Generated from Empori.Typings.TypescriptTyping
 */

/**
 * Map properties suited for usage within JavaScript.
 */
export interface GoogleMapsJson {

  /**
   * The map height in pixels.
   */
  height: number;

  /**
   * The API key for Google Maps, specified in the Umbraco back-office.
   */
  apiKey: string;

  /**
   * Iframe HTML to replace the JavaScript integration.
   */
  iframeHtml: string;

  /**
   * Initial map zoom.
   */
  initialZoom: number;

  /**
   * Initial map latitude.
   */
  initialLat: number;

  /**
   * Initial map longitude.
   */
  initialLng: number;

  /**
   * A list of map markers.
   */
  markerCollection: GoogleMapsMarkers;
}

/**
 * Used for easier JSON conversion.
 */
export interface GoogleMapsMarkers {

  /**
   * A list of map markers.
   */
  markers: Marker[];
}

/**
 * A single map marker item.
 */
export interface Marker {

  /**
   * The marker's address.
   */
  address: string;

  /**
   * The marker's latitude on the map.
   */
  lat: number;

  /**
   * The marker's longitude on the map.
   */
  lng: number;

  /**
   * The marker's description. Shows upon hoovering on the marker.
   */
  description: string;
}

/**
 * Used for ECMS cookie window content.
 */
export interface CookiesViewModel {

  /**
   * The main header of the cookie window content.
   */
  headerText: string;

  /**
   * The information text.
   */
  text: string;

  /**
   * The displayed label of the accept button.
   */
  acceptButtonText: string;

  /**
   * The displayed label of the read more button.
   */
  readMoreButtonText: string;

  /**
   * The link for the read more button destination.
   */
  readMoreButtonLink: string;
}

/**
 * Hidden text properties suited for usage within JavaScript.
 */
export interface HiddenTextJson {

  /**
   * The cap height of the text in pixels.
   */
  height: number;

  /**
   * Button label when the text is capped.
   */
  showText: string;

  /**
   * Button label when the text is visible.
   */
  hideText: string;
}

/**
 * The model for the entire buttons object. Used now for easier JSON conversion.
 */
export interface ButtonsModel {

  /**
   * The actual buttons.
   */
  buttons: ButtonModel[];
}

/**
 * Standard button properties.
 */
export interface ButtonModel {

  /**
   * The button label.
   */
  text: string;

  /**
   * The link url / href value.
   */
  url: string;

  /**
   * Reference to the FontAwesome icon library.
   */
  iconHtml: string;

  /**
   * If the button's @see Empori.Razor.Controls.ButtonModel.Url should open in a new browser window.
   */
  newWindow: boolean;

  /**
   * Removes the CSS background colors and padding and makes the button look more like plain text.
   */
  displayAsText: boolean;

  /**
   * Uses the alternative color as background color. @see Empori.Core.Configuration.Options.DesignOptions.SecondaryColor
   */
  inverted: boolean;

  /**
   * Links that contains a hash (#) or query (?).
   */
  anchor: string;
}

/**
 * A single weekday item.
 */
export interface OpenHoursDayItem {

  /**
   * What label to show for the day. Usually its the actual day (e.g. Monday).
   */
  dayLabel: string;

  /**
   * When the facility opens this day. Can have any string value.
   * TODO: Try to convert this to a date and use JavaScript for some more useful stuff.
   */
  openHour: string;

  /**
   * When the facility closes this day. Can have any string value.
   * TODO: Try to convert this to a date and use JavaScript for some more useful stuff.
   */
  closeHour: string;
}

/**
 * The model for the list of links.
 */
export interface SocialLinksModel {

  /**
   * The list.
   */
  links: SocialLink[];
}

/**
 * The social link item.
 */
export interface SocialLink {

  /**
   * A pre-defined icon reference.
   */
  linkIcon: LinkIcon;

  /**
   * FontAwesome icon HTML.
   */
  icon: string;

  /**
   * The link label.
   */
  linkLabel: string;

  /**
   * The link source specified by the user.
   */
  linkUrl: string;

  /**
   * The external link to the social media page or platform, forced into https protocol.
   */
  url: string;
}

/**
 * A selection of common social media platforms.
 */
export enum LinkIcon {

  /**
   * Facebook.
   */
  facebook = 0,

  /**
   * Twitter.
   */
  twitter = 1,

  /**
   * Instagram.
   */
  instagram = 2,

  /**
   * LinkedIn.
   */
  linkedIn = 3,

  /**
   * YouTube.
   */
  youtube = 4,

  /**
   * Pinterest.
   */
  pinterest = 5,

  /**
   * Default.
   */
  default = 99
}

/**
 * The model for the entire FAQ-items object. Used now for easier JSON conversion.
 */
export interface FaqModel {

  /**
   * The FAQ items list.
   */
  faqItems: FaqItem[];
}

/**
 * A single FAQ item.
 */
export interface FaqItem {

  /**
   * The question that shows in the block header.
   */
  question: string;

  /**
   * The answer that can be toggled when the @see Empori.Razor.Controls.FaqItem.Question is clicked.
   */
  answer: string;

  /**
   * The icon to show next to the @see Empori.Razor.Controls.FaqItem.Question.
   */
  iconHtml: string;

  /**
   * If the icon should rotate when the answer is toggled.
   */
  enableIconRotate: boolean;
}

/**
 * A general input item primarily used for @see Empori.Core.Data.Entities.Form.
 */
export interface InputItem {

  /**
   * This identification is used for frontend drag and drop only.
   */
  id: number;

  /**
   * The unique name that is associated with the value.
   */
  uniqueName: string;

  /**
   * Name is used for frontend reference only.
   */
  name: string;

  /**
   * Single value pre-defined by admin or assigned by the end-user.
   */
  value: string;

  /**
   * Multiple values pre-defined by admin or assigned by the end-user.
   */
  values: string[];

  /**
   * A shorter descriptive text about the input.
   */
  label: string;

  /**
   * Type of HTML input.
   */
  type: EmporiInputType;

  /**
   * A descriptive text within the input.
   */
  placeholder: string;

  /**
   * A descriptive text associated to the input.
   */
  description: string;

  /**
   * If the input should be required for form submission.
   */
  required: boolean;

  /**
   * Validation member translates to "pattern" HTML attribute and only works with the following input types: text, date, search, url, tel, email, and password.
   */
  validation: string;

  /**
   * The global title attribute helps describe the pattern to the end user.
   */
  title: string;

  /**
   * Alternatives member is only relevant for select, radio and checkbox input types.
   */
  alternatives: InputAlternative[];

  /**
   * Only relevant for input-type file to specify what filetypes are allowed
   */
  allowedFiles: AllowedFileType[];

  /**
   * If this member is true, form recipient specified in global and individual settings are ignored.
   * The mailto recipient gets taken from the selected "Alternatives" > "MailTo" value.
   * Only one input in each form can have this member set as true.
   */
  controlsRecipients: boolean;

  /**
   * FontAwesome string reference to an icon in their library. Primarily used for back-office frontend.
   */
  icon: string;

  /**
   * How much space this input should take in the horizontal context of the parent element.
   */
  width: WidthLayout;

  /**
   * If this member is true, the associated @see Empori.Core.Controllers.Models.EmporiInputType.Email input will send a confirmation e-mail to the user.
   */
  sendConfirmationEmail: boolean;

  /**
   * If this is true the first option will set as default empty option
   */
  isDefaultEmptyOptionSelected: boolean;
}

/**
 * Various HTML input types.
 */
export enum EmporiInputType {

  /**
   * <input type="text">
   */
  text = 0,

  /**
   * <textarea></textarea>
   */
  textArea = 1,

  /**
   * <input type="email">
   */
  email = 2,

  /**
   * <input type="date">
   */
  date = 3,

  /**
   * <select></select>
   */
  select = 4,

  /**
   * <input type="number">
   */
  number = 5,

  /**
   * <input type="checkbox">
   */
  checkbox = 6,

  /**
   * <input type="radio">
   */
  radiobutton = 7,

  /**
   * <input type="submit">
   */
  submit = 8,

  /**
   * <input type="file">
   */
  file = 9,

  /**
   * Custom HTML within the <form> element.
   */
  rte = 10,

  /**
   * <input type="time">
   */
  time = 11
}

/**
 * Multiple input alternatives that holds a @see Empori.Core.Controllers.Models.InputAlternative.Name and a @see Empori.Core.Controllers.Models.InputAlternative.MailTo address if @see Empori.Core.Controllers.Models.InputItem.ControlsRecipients is active for this input.
 */
export interface InputAlternative {

  /**
   * The display name of the alternative that can be selected or viewed.
   */
  name: string;

  /**
   * If the input @see Empori.Core.Controllers.Models.InputItem.ControlsRecipients is used for an input, this value can hold an e-mail address associated to the alternative @see Empori.Core.Controllers.Models.InputAlternative.Name.
   */
  mailTo: string;
}

export interface AllowedFileType {
  type: string;
}

/**
 * A design reference that can be used for width rules.
 */
export enum WidthLayout {

  /**
   * Most commonly used for setting a 50 percentage width to something.
   */
  half = 0,

  /**
   * Most commonly used for setting a 100 percentage width to something.
   */
  full = 1
}

/**
 * An image gallery model suited for usage within JavaScript.
 */
export interface ImageGalleryModel {

  /**
   * Properties used for the frontend editor.
   */
  imageGalleryEditorModel: ImageGalleryEditorModel;

  /**
   * The image gallery's delay before showing the next slide if @see Empori.Razor.Controls.ImageGalleryModel.Autoplay is active.
   */
  delay: number;

  /**
   * The slides height. If no value is chosen the height automatically fits the content.
   */
  staticHeight: number;

  /**
   * Whether to autoplay the gallery or not.
   */
  autoplay: boolean;

  /**
   * JavaScript id reference.
   */
  macroId: string;

  /**
   * The slides margin. If no value is chosen the margin is automatically set to 0.
   */
  spaceBetween?: number;

  /**
   * The amount of slides displayed. If no value is chosen only one slide is shown at a time.
   */
  slidesPerView?: number;

  /**
   * A setting for enabling fullscreen mode for the entire gallery.
   */
  fullscreenEnabled: boolean;
}

/**
 * The image gallery model for the frontend editor.
 */
export interface ImageGalleryEditorModel {

  /**
   * A list of slides.
   */
  slides: Slide[];

  /**
   * An extra (optional field) to show beneath the slides.
   */
  extraInfo: GalleryExtraInfoModel;
}

/**
 * A slide item.
 */
export interface Slide {

  /**
   * The image source path.
   */
  image: string;

  /**
   * A fallback image source path to empty videos.
   */
  poster: string;

  /**
   * The main title above the @see Empori.Razor.Controls.Slide.Image.
   */
  titleText: string;

  /**
   * The subtitle beneath the @see Empori.Razor.Controls.Slide.TitleText.
   */
  subtitleText: string;

  /**
   * If set to true, it adds an overlay element between the @see Empori.Razor.Controls.Slide.Image and the text elements (@see Empori.Razor.Controls.Slide.TitleText and @see Empori.Razor.Controls.Slide.SubtitleText).
   */
  isImageOverlay: boolean;

  /**
   * The individual slide's buttons.
   */
  buttonsModel: ButtonsModel;

  /**
   * The alignment of the content.
   */
  contentAlignment: ContentAlignment;
  verticalAlignment: VerticalAlignment;
}

/**
 * Various alignment alternatives.
 */
export enum ContentAlignment {

  /**
   * Align to the left.
   */
  left = 0,

  /**
   * Align to the horizontal center.
   */
  center = 1,

  /**
   * Align to the right.
   */
  right = 2
}

export enum VerticalAlignment {
  top = 0,
  center = 1,
  bottom = 2
}

/**
 * The model for the slide's extra content suited for usage within JavaScript.
 */
export interface GalleryExtraInfoModel {

  /**
   * Whether to show or hide the extra info.
   */
  isHideExtraInfo: boolean;

  /**
   * The extra info window's columns.
   */
  columns: GalleryExtraInfoColumn[];
}

/**
 * A column item that is used within the @see Empori.Razor.Controls.GalleryExtraInfoModel.
 */
export interface GalleryExtraInfoColumn {

  /**
   * Image source path.
   */
  image: string;

  /**
   * HTML content.
   */
  content: string;

  /**
   * HTML title.
   */
  title: string;
}

/**
 * The counter model for JavaScript usage.
 */
export interface CounterJson {

  /**
   * DOM reference.
   */
  id: string;

  /**
   * Count from this value.
   */
  countFrom: number;

  /**
   * Count to this value.
   */
  countTo: number;

  /**
   * How long the counting should take in milliseconds.
   */
  time: number;

  /**
   * A frontend check whether the counting has started or not.
   */
  started: boolean;
}

/**
 * Properties to be used in JavaScript.
 */
export interface FormDetails {

  /**
   * The id of the form.
   */
  formId: string;

  /**
   * The form submit button id.
   */
  formSubmitButtonId: string;

  /**
   * The actual form with its child properties.
   */
  form: Form;

  /**
   * File size cap in MiB (MB).
   */
  globalMaxUploadFileSize?: number;

  /**
   * If the visitor should remain on the same page or not upon form submission.
   */
  stayOnPage: boolean;

  /**
   * DOM reference to the element containing the message HTML.
   */
  messageKey: string;

  /**
   * DOM reference to the element containing the post status messages for "posting form" and "form posted".
   */
  statusKey: string;

  /**
   * The URL to post data to.
   */
  fetchUrl: string;

  /**
   * The URL to fetch empori captcha
   */
  fetchUrlEmporiCaptcha: string;

  /**
   * The URL to post data to.
   */
  spamProtectionDetails: SpamProtectionDetails;
}

/**
 * Turnstile response
 */
export interface SpamProtectionDetails {

  /**
   * Check whether tunstile spam protection is on from the settings
   */
  isTurnstileEnabled: boolean;

  /**
   * Check whether empori spam protection is enabled from the settings
   */
  isEmporiCaptchaEnabled: boolean;

  /**
   * Empori captcha generating url
   */
  fetchUrlEmporiCaptcha: string;

  /**
   * Empori captcha image guid id
   */
  captchaImgId: string;
}

/**
 * A standard image model additional information.
 */
export interface ImageWithInfo {

  /**
   * Image media path.
   */
  imgUrl: string;

  /**
   * A non-cropped or otherwise manipulated image url from Umbraco.
   */
  imgUrlOriginal: string;

  /**
   * Image alt HTML attribute.
   */
  imgAlt: string;

  /**
   * A title to render alongside the image. Not the same as @see Empori.Razor.Models.ImageWithInfo.ImgAlt.
   */
  title: string;

  /**
   * URL to the content node/page.
   */
  pageLink: string;
}

export interface SearchAliasItemModel {
  searchAliasItems: SearchAliasItem[];
}

export interface SearchAliasItem {
  alias: string;
  fieldDescription: string;
  isRemovedFromSearch: boolean;
  boostValue: string;
  culture: string;
}

export interface UmbracoSearchModel {
  culture: string;
  selectedNodesIdList: number[];
  displayTitleAlias: string;
  displayDescriptionAlias: string;
  pageSize?: number;
  searchAliasItems: SearchAliasItem[];
  placeHolderText: string;
  noResultFoundText: string;
}

export interface BrandModelData {
  title: string;
  year: string;
  link: string;
  lang: string;
}

export interface BrandModelItem {
  name: string;
  year: string;
  description: string;
  pricePrefix: string;
  priceFrom?: number;
  mainImage: string;
  created: string;
  link: string;
  brand: string;
  family: string;
  category: string;
  imageCrop: string;
}

export interface BrandPagesConfig {
  lang: string;
  translations: BrandPagesTranslations;
}

export interface BrandPagesTranslations {
  viewingModelsCount: string;
  filter: string;
  sort: string;
  showingAllBrands: string;
  showingAllFamilies: string;
  showingAllCategories: string;
  closeAndShowModels: string;
  alphabetAsc: string;
  alphabetDesc: string;
  priceAsc: string;
  priceDesc: string;
  createdAsc: string;
  createdDesc: string;
  noModelsFound: string;
}

export enum RowAnimationStyle {
  none = 0,
  fadeUp = 1
}

export interface LogotypesJson {
  mediaObjects: ImageWithInfo[];
  logotypesPerView?: number;
  spaceBetween?: number;
  display: LogotypesDisplay;
  hideNavigation: boolean;
  autoplay: boolean;
  externalLink?: boolean;
  maxHeight?: number;
  filterColor: FilterColor;
  buttonLabel: string;
}

export enum LogotypesDisplay {
  slideshow = 0,
  grid = 1,
  partiallyHiddenGrid = 2
}

export enum FilterColor {
  none = 0,
  black = 1,
  gray = 2,
  white = 3
}

export interface InteractiveImageObj {
  image: string;
  buttons: ButtonObj[];
}

export interface ButtonObj {
  id: string;
  index: number;
  positionX: number;
  positionY: number;
  featureInput: FeatureInput;
}

export interface FeatureInput {
  sortingTitle: string;
  inputText: string;
  active: boolean;
}

export interface PopupViewModel {
  active?: boolean;
  showInterval: number;
  expirationDate?: string;
  appearOnScroll?: number;
  name: string;
}

export interface FormPostResponse {
  posts: FormPost[];
}

export interface FormExportQueryString {
  formId: number;
  culture: string;
  format?: SpreadsheetFormat;
}

/**
 * Supported spreadsheet formats.
 */
export enum SpreadsheetFormat {

  /**
   * Default XML-based file format for Excel 2010 and Excel 2007.
   */
  xlsx = 0,

  /**
   * Excel 97 - Excel 2003 Binary file format (BIFF8).
   */
  xls = 1,

  /**
   * Comma separated values.
   */
  csv = 2,

  /**
   * JavaScript object notation.
   */
  json = 3
}

export interface ResendFormPostsMailResponse {
  failedResentMails: FailedResentMail[];
  succededResentMails: number[];
}

export interface FailedResentMail {
  id: number;
  errorMessage: string;
}

export interface OptionsResponse {
  key: string;
  title: string;
  definitions: ObjectDefinition[];
  properties: OptionsProperty[];
  model: { [key: string]: any };
}

export interface ObjectDefinition {
  type: string;
  propertyEditor: string;
  formatString: string;
  properties: OptionsProperty[];
}

export interface OptionsProperty {
  title: string;
  description: string;
  key: string;
  type: string;
  propertyType: OptionsPropertyType;
  tab: string;
  group: string;
  relation: OptionsRelation;
  isAdminOnly?: boolean;
  isVaryByCulture?: boolean;
  evaluateOnChange?: boolean;
  mappingTitle: string;
}

/**
 * Options property type of an object definition.
 */
export enum OptionsPropertyType {

  /**
   * Primitive value such as a number or a string.
   */
  primitive = 0,

  /**
   * Nested object structure.
   */
  object = 1,

  /**
   * Array of nested object structures.
   */
  array = 2
}

export interface OptionsRelation {
  multiSelect?: boolean;
  optional?: boolean;
  style: number;
  values: IOptionsValue[];
}

/**
 * Selectable options relation value.
 */
export interface IOptionsValue {

  /**
   * Title of value.
   */
  title: string;

  /**
   * Options value
   */
  value: string;

  /**
   * Description to show to the editor.
   */
  description: string;
  isVaryByCulture?: boolean;

  /**
   * Indicate that this value is selected.
   */
  selected?: boolean;
}

/**
 * Type of list style to use for relation values.
 */
export enum RelationListStyle {

  /**
   * A single or a multi select dropdown list.
   */
  dropdown = 0,

  /**
   * A list of radio buttons or a list of checkboxes for multi select.
   */
  buttons = 1
}

export interface MenuOptionsItem {
  title: string;
  key: string;
  icon: string;
  subMenu: MenuOptionsItem[];
}

export interface JobDescriptionResponse {
  jobDescriptions: RecurringJobDescription[];
  latestRuns: ScheduledJobRun[];
}

export interface JobTypesResponse {
  types: JobTypeModel[];
}

export interface JobTypeModel {
  typeName: string;
  name: string;
}

/**
 * Job reporter event data from the reporter channel.
 */
export interface JobReporterEvent {

  /**
   * Gets or sets scheduled job entity id.
   */
  entityId: number;

  /**
   * Gets or sets progress value.
   */
  progress: number;

  /**
   * Gets or sets status text.
   */
  text: string;

  /**
   * Gets or sets job reporter status.
   */
  status: JobReportStatus;

  /**
   * Gets or sets internal job status that may be used to report metadata status to listeners.
   */
  metadataJobStatus?: MetadataJobStatus;
}

/**
 * Job reporter status for a job task during its execution.
 */
export enum JobReportStatus {

  /**
   * The job task is idling and waiting to be ran.
   */
  idle = 0,

  /**
   * The job task is currently running.
   */
  running = 1
}

/**
 * Metadata job status.
 */
export enum MetadataJobStatus {

  /**
   * Job task is removed and should be propagated to listeners.
   */
  remove = 0,

  /**
   * Job task is updated and should be propagated to listeners.
   */
  update = 1,

  /**
   * Job task is created and should be propagated to listeners.
   */
  create = 2
}

/**
 * Job reporter status summary of individual job tasks.
 */
export interface JobReporterStatusSummary {

  /**
   * Job task events by entity id.
   */
  jobEvents: { [key: number]: JobReporterEvent };
}

export interface JobDescriptionUpdateRequest {
  model: JobTaskModel;
}

export interface JobTaskModel {

  /**
   * Scheduled job entity id.
   */
  entityId: number;

  /**
   * Given name of this job task.
   */
  name: string;

  /**
   * Cron timing expression.
   */
  cron: string;

  /**
   * Cron time zone in IANA id. UTC if `null`.
   */
  cronTimeZoneIana: string;

  /**
   * Decide whether this job task is active or not.
   */
  active: boolean;

  /**
   * Type name of this job task.
   */
  type: string;
}

export interface JobRunLogsResponse {
  run: ScheduledJobRun;
  logs: ScheduledJobRunLog[];
}

export interface JobRunsResponse {
  runs: ScheduledJobRun[];
}

export interface EventLog {
  message: string;
  exceptionMessage: string;
  exceptionStackTrace: string;
  exceptionSource: string;
}

export interface RichTextConfiguration {
  editor: any;
  overlaySize: string;
  hideLabel: boolean;
  mediaParentId: GuidUdi;
  ignoreUserStartNodes: boolean;
}

export interface GuidUdi extends Udi {
  guid: string;
  isRoot: boolean;
}

export interface Udi {
  uriValue: Uri;
  entityType: string;
  isRoot: boolean;
}

export interface Uri {
  absolutePath: string;
  absoluteUri: string;
  localPath: string;
  authority: string;
  hostNameType: UriHostNameType;
  isDefaultPort: boolean;
  isFile: boolean;
  isLoopback: boolean;
  pathAndQuery: string;
  segments: string[];
  isUnc: boolean;
  host: string;
  port: number;
  query: string;
  fragment: string;
  scheme: string;
  originalString: string;
  dnsSafeHost: string;
  idnHost: string;
  isAbsoluteUri: boolean;
  userEscaped: boolean;
  userInfo: string;
}

export enum UriHostNameType {
  unknown = 0,
  basic = 1,
  dns = 2,
  iPv4 = 3,
  iPv6 = 4
}

export interface SearchResultResponse {
  totalCount: number;
  searchTerm: string;
  results: SearchResultItem[];
}

export interface SearchResultItem {
  title: string;
  description: string;
  url: string;
  id: string;
  contentType: string;
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IForm
 */
export interface Form extends EntityBase {

  /**
   * @inheritdoc 
   */
  name: string;
  inputs: InputItem[];
  settings: FormSettings;
}

export interface FormSettings extends BaseOptions {
  submitLabel: string;

  /**
   * Page node to redirect to after form has been posted. See also @see Empori.Core.Middlewares.FormPostMiddleware.
   */
  redirectPageNode: BaseNodePickerOption;

  /**
   * File size in megabytes (MiB).
   */
  maxUploadFileSize?: number;
  adminMailOptions: AdminEmailOptions;
  spamProtectionOptions: FormSpamProtectionOptions;
}

/**
 * Class contains the bare-minumum fields required for NodePicker property-editor to work.
 * Can be extended with all the fields listed in @see Empori.Core.Configuration.PropertyEditors.BaseNodePickerOption
 */
export interface BaseNodePickerOption {
  nodeId: number;
  name: string;
  icon: string;
}

export interface AdminEmailOptions extends MailToOptions {
  sendAdminEmail: GlobalNoYes;
  attachUploadedFiles: GlobalNoYes;
}

/**
 * No/yes override for a global @see System.Boolean option.
 */
export enum GlobalNoYes {

  /**
   * Use global option.
   */
  global = 0,

  /**
   * No.
   */
  no = 1,

  /**
   * Yes.
   */
  yes = 2
}

/**
 * Options for an email message with receiver address, sender address, template and subject.
 */
export interface MailToOptions extends MailOptions {

  /**
   * @inheritdoc 
   */
  toEmail: string;
}

/**
 * Options for an email message with sender address, template and subject.
 */
export interface MailOptions {

  /**
   * @inheritdoc 
   */
  fromEmail: string;

  /**
   * @inheritdoc 
   */
  template: string;

  /**
   * @inheritdoc 
   */
  title: string;
}

/**
 * emporicaptcha/turnstile override for a global @see System.Boolean option.
 */
export enum FormSpamProtectionOptions {

  /**
   * Use global option.
   */
  global = 0,
  none = 1,

  /**
   * Empori Captcha.
   */
  emporiCaptcha = 2,

  /**
   * 3rd Party Turnstile.
   */
  turnstile = 3
}

/**
 * Base abstract class of a configurable @see Microsoft.Extensions.Options.IOptions`1 in the backoffice.
 */
export interface BaseOptions {
}

export interface EntityBase extends BeingDirtyBase {
  id: number;
  key: string;
  createDate: string;
  updateDate: string;
  deleteDate?: string;
  hasIdentity: boolean;
}

export interface BeingDirtyBase {
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPost
 */
export interface FormPost extends EntityBase {

  /**
   * @inheritdoc 
   */
  formId: number;

  /**
   * @inheritdoc 
   */
  pageId?: number;
  pageCulture: string;

  /**
   * @inheritdoc 
   */
  ip: string;

  /**
   * @inheritdoc 
   */
  created: string;
  status: number;

  /**
   * @inheritdoc 
   */
  postData: FormPostData[];

  /**
   * @inheritdoc 
   */
  postFiles: FormPostFile[];
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostData
 */
export interface FormPostData extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  name: string;

  /**
   * @inheritdoc 
   */
  value: string;
}

/**
 * @inheritdoc Empori.Core.Data.Entities.IFormPostFile
 */
export interface FormPostFile extends EntityBase {

  /**
   * @inheritdoc 
   */
  formPostId: number;

  /**
   * @inheritdoc 
   */
  fieldName: string;

  /**
   * @inheritdoc 
   */
  fileName: string;

  /**
   * @inheritdoc 
   */
  length: number;

  /**
   * @inheritdoc 
   */
  contentType: string;

  /**
   * @inheritdoc 
   */
  storageName: string;
}

export interface ScheduledJob extends EntityBase {
  active: boolean;
  lastRun: string;
  name: string;
  type: string;
  cron: string;
  cronTimeZoneIana: string;
}

export interface ScheduledJobRun extends EntityBase {
  jobId: number;
  result: JobRunResult;
  startDate: string;
  endDate?: string;
}

/**
 * Job run result.
 */
export enum JobRunResult {

  /**
   * Default idle result.
   */
  idle = 0,

  /**
   * Run is currently running.
   */
  running = 1,

  /**
   * Run is completed.
   */
  complete = 2,

  /**
   * Run is stopped.
   */
  stopped = 3,

  /**
   * Run has an error.
   */
  error = 4
}

export interface ScheduledJobRunLog extends EntityBase {
  runId: number;
  level: LogLevel;
  message: string;
}

export enum LogLevel {
  trace = 0,
  debug = 1,
  information = 2,
  warning = 3,
  error = 4,
  critical = 5,
  none = 6
}

/**
 * A recurring job description that spawns @see Empori.Core.Scheduler.IJobTasks in an interval.
 */
export interface RecurringJobDescription {

  /**
   * @inheritdoc 
   */
  lastRun?: string;

  /**
   * @inheritdoc 
   */
  nextOccurrence?: string;

  /**
   * @inheritdoc 
   */
  active: boolean;

  /**
   * @inheritdoc 
   */
  name: string;

  /**
   * @inheritdoc 
   */
  entity: ScheduledJob;

  /**
   * @inheritdoc 
   */
  currentRunEntity: ScheduledJobRun;

  /**
   * @inheritdoc 
   */
  status: JobReportStatus;
}

